/**
 * @fileoverview Landing page component for unauthenticated users.
 * Displays product marketing content, feature highlights, and login buttons.
 * Handles Google OAuth login flow and displays any authentication errors.
 * Shows app version in the footer.
 * @module pages/landing
 */

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, Bell, Zap, AlertCircle } from "lucide-react";
import { BrandIcon } from "@/components/brand-icon";
import { useAuth } from "@/hooks/useAuth.js";
import { useState, useEffect } from "react";

// @ts-ignore - Vite special import for app version
const version = __APP_VERSION__;

/**
 * Landing page component.
 * Public page shown to unauthenticated users with product information
 * and login functionality. Triggers Google OAuth popup on login button click.
 * @returns Landing page JSX
 */
export default function Landing() {
  const { login, isLoading } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error) {
      if (error === "auth_failed") setLoginError("Authentication failed. Please try again.");
      else if (error === "registration_failed") setLoginError("Failed to register user.");
      else setLoginError("An unknown error occurred.");

      // Clear error from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Handles login button click.
   * Triggers Google OAuth redirect flow.
   */
  const handleLogin = () => {
    login();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="border-b" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-testid="landing-header-container">
          <div className="flex items-center gap-2" data-testid="landing-header-brand">
            <BrandIcon className="h-6 w-6 text-primary" data-testid="landing-header-logo-icon" />
            <span className="text-lg font-semibold" data-testid="landing-header-brand-text">Clockwork</span>
          </div>
          <Button onClick={handleLogin} disabled={isLoading} data-testid="landing-header-login-button">
            {isLoading ? "Loading..." : "Log In"}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="landing-main-content">
        {loginError && (
          <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex gap-3" data-testid="landing-login-error">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" data-testid="landing-login-error-icon" />
            <div data-testid="landing-login-error-content">
              <p className="font-medium text-destructive" data-testid="landing-login-error-title">Authentication Error</p>
              <p className="text-sm text-destructive/80 mt-1" data-testid="landing-login-error-message">{loginError}</p>
            </div>
          </div>
        )}
        <div className="py-20 text-center" data-testid="landing-hero-section">
          <h1 className="text-4xl font-semibold text-foreground mb-4" data-testid="landing-hero-title">
            Monitor Your APIs with Confidence
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8" data-testid="landing-hero-subtitle">
            Real-time uptime monitoring, latency tracking, and instant alerts.
            Setup takes less than 30 seconds.
          </p>
          <Button size="lg" onClick={handleLogin} disabled={isLoading} data-testid="landing-hero-cta-button">
            {isLoading ? "Loading..." : "Get Started"}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 pb-20" data-testid="landing-features-grid">
          <Card className="p-6" data-testid="landing-feature-quick-setup-card">
            <div className="flex items-center gap-3 mb-3" data-testid="landing-feature-quick-setup-header">
              <div className="p-2 bg-primary/10 rounded-md" data-testid="landing-feature-quick-setup-icon-container">
                <Zap className="h-5 w-5 text-primary" data-testid="landing-feature-quick-setup-icon" />
              </div>
              <h3 className="font-medium" data-testid="landing-feature-quick-setup-title">Quick Setup</h3>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="landing-feature-quick-setup-description">
              Add your URL and schedule. Start monitoring in under 30 seconds.
            </p>
          </Card>

          <Card className="p-6" data-testid="landing-feature-live-metrics-card">
            <div className="flex items-center gap-3 mb-3" data-testid="landing-feature-live-metrics-header">
              <div className="p-2 bg-primary/10 rounded-md" data-testid="landing-feature-live-metrics-icon-container">
                <BrandIcon className="h-5 w-5 text-primary" data-testid="landing-feature-live-metrics-icon" />
              </div>
              <h3 className="font-medium" data-testid="landing-feature-live-metrics-title">Live Metrics</h3>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="landing-feature-live-metrics-description">
              Beautiful graphs showing latency over time with pass/fail status.
            </p>
          </Card>

          <Card className="p-6" data-testid="landing-feature-instant-alerts-card">
            <div className="flex items-center gap-3 mb-3" data-testid="landing-feature-instant-alerts-header">
              <div className="p-2 bg-primary/10 rounded-md" data-testid="landing-feature-instant-alerts-icon-container">
                <Bell className="h-5 w-5 text-primary" data-testid="landing-feature-instant-alerts-icon" />
              </div>
              <h3 className="font-medium" data-testid="landing-feature-instant-alerts-title">Instant Alerts</h3>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="landing-feature-instant-alerts-description">
              Get notified immediately when your endpoints go down.
            </p>
          </Card>

          <Card className="p-6" data-testid="landing-feature-flexible-scheduling-card">
            <div className="flex items-center gap-3 mb-3" data-testid="landing-feature-flexible-scheduling-header">
              <div className="p-2 bg-primary/10 rounded-md" data-testid="landing-feature-flexible-scheduling-icon-container">
                <Clock className="h-5 w-5 text-primary" data-testid="landing-feature-flexible-scheduling-icon" />
              </div>
              <h3 className="font-medium" data-testid="landing-feature-flexible-scheduling-title">Flexible Scheduling</h3>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="landing-feature-flexible-scheduling-description">
              Monitor every minute or hourly. Custom headers and request bodies.
            </p>
          </Card>
        </div>
      </main>

      <footer className="border-t mt-auto" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-testid="landing-footer-container">
          <p className="text-sm text-muted-foreground" data-testid="landing-footer-text">
            Built for developers who value reliability
          </p>
          <p className="text-xs text-muted-foreground/60" data-testid="landing-footer-version">
            v{version}
          </p>
        </div>
      </footer>
    </div>
  );
}
